import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BotanicalCorner, GoldLineDivider, FlowingWaves, MandalaOrnament } from '@/components/feature/DrawingLines';

const protocols = [
  { step: '01', title: '시술 전 멸균 준비', desc: '모든 도구는 고압 증기 멸균기(오토클레이브)로 처리 후 개봉. 바늘·잉크컵·장갑은 1회용 전용 키트 사용.', icon: 'ri-test-tube-line' },
  { step: '02', title: '작업 공간 소독', desc: '시술 베드, 트롤리, 조명 등 모든 접촉 표면을 의료용 소독제로 닦아냅니다. UV-C 공기 살균기 상시 가동.', icon: 'ri-bubble-chart-line' },
  { step: '03', title: '아티스트 위생 준수', desc: '시술 전 의료용 손 소독 후 멸균 장갑 착용. 시술 중 장갑 교체 기준 엄수. 마스크 착용 필수.', icon: 'ri-hand-sanitizer-line' },
  { step: '04', title: '시술 진행', desc: '1회용 니들 개봉 후 고객 앞에서 확인. 잉크는 1회용 잉크컵에 소분하여 사용. 남은 잉크는 즉시 폐기.', icon: 'ri-pencil-ruler-2-line' },
  { step: '05', title: '시술 후 폐기 처리', desc: '사용된 모든 1회용 도구는 의료 폐기물 전용 용기에 즉시 폐기. 날카로운 도구는 전용 샤프 컨테이너 사용.', icon: 'ri-delete-bin-line' },
  { step: '06', title: '사후 소독 & 기록', desc: '시술 완료 후 작업 공간 재소독. 시술 기록 및 위생 체크리스트 작성 보관. 정기 본사 감리 실시.', icon: 'ri-file-check-line' },
];

const certifications = [
  { icon: 'ri-award-fill', title: 'WHO 기준 초과', desc: '세계보건기구 위생 기준을 초과하는 자체 프로토콜 적용' },
  { icon: 'ri-hospital-fill', title: '의료용 소독제', desc: '병원급 의료용 소독제만 사용, 일반 소독제 사용 금지' },
  { icon: 'ri-shield-check-fill', title: '아티스트 인증제', desc: '본사 위생 인증 시험 통과 아티스트만 시술 자격 부여' },
  { icon: 'ri-microscope-line', title: '피부과 검증 잉크', desc: '피부과 전문의 공동 개발, 중금속 무함유 저자극 잉크' },
];

const inkFeatures = [
  { label: '중금속 무함유', icon: 'ri-close-circle-line', positive: false },
  { label: '저자극 성분', icon: 'ri-leaf-line', positive: true },
  { label: '피부과 검증', icon: 'ri-stethoscope-line', positive: true },
  { label: '발색력 최상급', icon: 'ri-palette-line', positive: true },
  { label: '장기 지속성', icon: 'ri-time-line', positive: true },
  { label: '민감성 피부 적합', icon: 'ri-heart-pulse-line', positive: true },
];

function SafetyHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  return (
    <section className="relative pt-40 pb-24 bg-[#1A1A1A] overflow-hidden">
      <div className="absolute inset-0 opacity-50">
        <img
          src="https://public.readdy.ai/ai/img_res/0bc720d0a18252f97461f976d27a37e8.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-transparent to-[#1A1A1A]/60" />
      <FlowingWaves color="rgba(201,169,110,0.1)" lineCount={4} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.2)" size={160} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />
      <div className="relative z-10 max-w-[1440px] mx-auto px-8 md:px-16 text-center">
        <div className="flex items-center justify-center gap-2 mb-10"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out' }}>
          <Link to="/brand" className="text-[10px] tracking-[0.3em] uppercase text-white/30 hover:text-white/60 transition-colors cursor-pointer" style={{ fontFamily: "'Montserrat', sans-serif" }}>Brand</Link>
          <span className="text-white/20 text-xs">/</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E]" style={{ fontFamily: "'Montserrat', sans-serif" }}>위생 및 안전 기준</span>
        </div>
        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out 0.15s' }}>
          <h1 className="text-4xl md:text-6xl text-white mb-6 leading-tight" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.1em' }}>
            위생 및 안전 기준
          </h1>
          <p className="text-sm md:text-base text-white/50 leading-relaxed max-w-lg mx-auto" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.8' }}>
            선율의 모든 가맹점은 동일한 Medical Grade 위생 프로토콜을 따릅니다.<br />
            타협 없는 안전이 우리의 첫 번째 약속입니다.
          </p>
        </div>
        <div className="mx-auto mt-12 h-px bg-white/15 transition-all duration-1000 ease-out" style={{ width: visible ? '80px' : '0px', transitionDelay: '500ms' }} />
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-10 md:gap-20"
          style={{ opacity: visible ? 1 : 0, transition: 'all 1s ease-out 0.6s' }}>
          {[{ value: 'WHO+', label: '위생 기준 초과' }, { value: '100%', label: '1회용 도구 사용' }, { value: '6단계', label: '위생 프로토콜' }].map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-2xl md:text-3xl text-white mb-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, letterSpacing: '-0.01em' }}>{s.value}</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/30" style={{ fontFamily: "'Montserrat', sans-serif" }}>{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProtocolSection() {
  const { ref, visible } = useScrollAnimation(0.05);
  return (
    <section className="py-28 md:py-36 bg-white overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-left" color="rgba(201,169,110,0.1)" size={160} />
      <BotanicalCorner position="bottom-right" color="rgba(201,169,110,0.08)" size={140} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="text-center mb-10" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}>
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Hygiene Protocol</p>
          <h2 className="text-3xl md:text-4xl text-[#222222] mb-5" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>6단계 위생 프로토콜</h2>
          <p className="text-sm text-[#888] max-w-md mx-auto leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>시술 전부터 후까지, 모든 단계에서 철저한 위생 관리가 이루어집니다.</p>
        </div>
        <div className="mb-12">
          <GoldLineDivider delay={0.2} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {protocols.map((p, i) => (
            <div key={p.step} className="group border border-[#EBEBEB] p-8 hover:border-[#222222] transition-all duration-300 cursor-default relative overflow-hidden"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s', transitionDelay: `${i * 80 + 200}ms` }}>
              <span className="absolute top-6 right-6 text-5xl font-thin text-[#F5F5F5] group-hover:text-[#EEEEEE] transition-colors select-none" style={{ lineHeight: 1, fontFamily: "'Montserrat', sans-serif" }}>{p.step}</span>
              <div className="w-10 h-10 flex items-center justify-center mb-5 border border-[#EBEBEB] group-hover:border-[#222222] group-hover:bg-[#222222] transition-all duration-300">
                <i className={`${p.icon} text-lg text-[#AAAAAA] group-hover:text-white transition-colors duration-300`} />
              </div>
              <h3 className="text-sm font-semibold text-[#222222] mb-3" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>{p.title}</h3>
              <p className="text-xs text-[#888] leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CertSection() {
  const { ref, visible } = useScrollAnimation(0.1);
  return (
    <section className="py-28 bg-[#FAFAFA] overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.1)" size={150} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-40px)', transition: 'all 1s ease-out' }}>
            <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Premium Ink</p>
            <h2 className="text-3xl md:text-4xl text-[#222222] mb-6 leading-tight" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>
              피부에 닿는 모든 것,<br /><span className="text-[#AAAAAA]">최고만을 사용합니다</span>
            </h2>
            <p className="text-sm text-[#888] leading-relaxed mb-8" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>자체 연구소에서 개발한 선율 전용 잉크는 피부과 전문의의 검증을 거쳤습니다. 중금속 무함유, 저자극 성분으로 구성되어 민감한 피부에도 안전합니다.</p>
            <div className="grid grid-cols-2 gap-3">
              {inkFeatures.map((f) => (
                <div key={f.label} className="flex items-center gap-2.5">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`${f.icon} text-sm ${f.positive ? 'text-[#C9A96E]' : 'text-[#888]'}`} />
                  </div>
                  <span className="text-xs text-[#555]" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(40px)', transition: 'all 1s ease-out 0.2s' }}>
            <img
              src="https://readdy.ai/api/search-image?query=premium%20tattoo%20ink%20bottles%20arranged%20on%20white%20marble%20surface%20professional%20product%20photography%20clean%20minimal%20background%20luxury%20aesthetic%20close%20up%20detail%20shot&width=700&height=560&seq=safety_ink1&orientation=landscape"
              alt="선율 프리미엄 잉크"
              className="w-full h-[420px] object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function CertBadges() {
  const { ref, visible } = useScrollAnimation(0.1);
  return (
    <section className="py-28 bg-white overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.1)" size={150} />
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="text-center mb-10" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}>
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Certifications</p>
          <h2 className="text-3xl md:text-4xl text-[#222222] mb-6" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}>선율의 안전 인증</h2>
          <div className="flex justify-center" style={{ opacity: visible ? 1 : 0, transition: 'opacity 1s ease 0.3s' }}>
            <MandalaOrnament size={70} color="rgba(201,169,110,0.3)" delay={0.3} />
          </div>
        </div>
        <div className="mb-12">
          <GoldLineDivider delay={0.4} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {certifications.map((c, i) => (
            <div key={c.title} className="text-center p-8 border border-[#EBEBEB] hover:border-[#C9A96E] transition-colors duration-300 cursor-default"
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, border-color 0.3s', transitionDelay: `${i * 100 + 200}ms` }}>
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5">
                <i className={`${c.icon} text-3xl text-[#C9A96E]`} />
              </div>
              <h3 className="text-sm font-semibold text-[#222222] mb-2" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif" }}>{c.title}</h3>
              <p className="text-xs text-[#888] leading-relaxed" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function BrandSafetyPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <SafetyHero />
        <ProtocolSection />
        <CertSection />
        <CertBadges />
      </main>
      <Footer />
    </div>
  );
}
